import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import { login as loginApi } from "../Services/AuthenticationService";
import { toast } from "react-toastify";
import ReAuthModal from "../Components/Common/ReAuthModal";

const AuthContext = createContext();
const MAX_REAUTH_ATTEMPTS = 2;
const STAY_MODAL_TIMEOUT_SECONDS = 10;
const INACTIVITY_TIMEOUT_MS = 1800_000;
const INACTIVITY_COUNTDOWN_SECONDS = 10;
const REAUTH_ATTEMPTS_KEY = "reauth_attempts";
const REAUTH_STAGE_KEY = "reauth_stage";

const STORED_PERMISSIONS_KEY = "userPermissions";
const STORED_MENU_ITEMS_KEY = "userMenuItems";

const getStoredAuth = () => {
  const storedToken = localStorage.getItem("accesstoken") || sessionStorage.getItem("accesstoken");
  const storedDecodedToken = localStorage.getItem("decodedToken") || sessionStorage.getItem("decodedToken");
  const storedPermissions = localStorage.getItem(STORED_PERMISSIONS_KEY);
  const storedMenuItems = localStorage.getItem(STORED_MENU_ITEMS_KEY);
  let parsedDecodedToken = null;
  let parsedPermissions = [];
  let parsedMenuItems = [];

  if (storedDecodedToken) {
    try {
      parsedDecodedToken = JSON.parse(storedDecodedToken);
    } catch {
      sessionStorage.removeItem("decodedToken");
    }
  }

  const hasExpiredToken = Boolean(parsedDecodedToken?.exp && parsedDecodedToken.exp * 1000 <= Date.now());

  if (hasExpiredToken) {
    localStorage.removeItem("accesstoken");
    localStorage.removeItem("decodedToken");
    sessionStorage.removeItem("accesstoken");
    sessionStorage.removeItem("decodedToken");
    sessionStorage.removeItem(REAUTH_ATTEMPTS_KEY);
    sessionStorage.removeItem(REAUTH_STAGE_KEY);
  }

  if (storedPermissions) {
    try {
      const parsed = JSON.parse(storedPermissions);
      parsedPermissions = Array.isArray(parsed) ? parsed : [];
    } catch {
      localStorage.removeItem(STORED_PERMISSIONS_KEY);
    }
  } else if (parsedDecodedToken?.Permissions || parsedDecodedToken?.permissions) {
    const raw = parsedDecodedToken.Permissions || parsedDecodedToken.permissions;
    parsedPermissions = Array.isArray(raw) ? raw : [];
  }

  if (storedMenuItems) {
    try {
      const parsed = JSON.parse(storedMenuItems);
      parsedMenuItems = Array.isArray(parsed) ? parsed : [];
    } catch {
      localStorage.removeItem(STORED_MENU_ITEMS_KEY);
    }
  }

  return {
    token: hasExpiredToken ? null : storedToken || null,
    decodedToken: hasExpiredToken ? null : parsedDecodedToken,
    permissions: parsedPermissions,
    menuItems: parsedMenuItems,
  };
};

const getStoredReAuthAttempts = () => {
  const parsedAttempts = Number(sessionStorage.getItem(REAUTH_ATTEMPTS_KEY));
  if (!Number.isFinite(parsedAttempts) || parsedAttempts < 0) {
    return 0;
  }
  return parsedAttempts;
};

const clearReAuthSessionState = () => {
  sessionStorage.removeItem(REAUTH_ATTEMPTS_KEY);
  sessionStorage.removeItem(REAUTH_STAGE_KEY);
};

export const AuthProvider = ({ children }) => {
  const initialAuth = getStoredAuth();

  const [token, setToken] = useState(initialAuth.token);
  const [decodedToken, setDecodedToken] = useState(initialAuth.decodedToken);
  const [permissions, setPermissions] = useState(initialAuth.permissions);
  const [menuItems, setMenuItems] = useState(initialAuth.menuItems);
  const [showStayModal, setShowStayModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [stayCountdown, setStayCountdown] = useState(STAY_MODAL_TIMEOUT_SECONDS);
  const [reAuthPassword, setReAuthPassword] = useState("");
  const [reAuthLoading, setReAuthLoading] = useState(false);
  const [reAuthError, setReAuthError] = useState("");
  const [reAuthAttempts, setReAuthAttempts] = useState(getStoredReAuthAttempts);
  const [showReAuthPassword, setShowReAuthPassword] = useState(false);
  const [showInactivityModal, setShowInactivityModal] = useState(false);
  const [inactivityCountdown, setInactivityCountdown] = useState(INACTIVITY_COUNTDOWN_SECONDS);
  const [isActiveSession, setIsActiveSession] = useState(true);
  const timerRef = useRef(null);
  const stayTimeoutRef = useRef(null);
  const stayIntervalRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const inactivityModalTimeoutRef = useRef(null);
  const inactivityIntervalRef = useRef(null);

  const clearStayModalTimers = useCallback(() => {
    if (stayTimeoutRef.current) {
      clearTimeout(stayTimeoutRef.current);
      stayTimeoutRef.current = null;
    }
    if (stayIntervalRef.current) {
      clearInterval(stayIntervalRef.current);
      stayIntervalRef.current = null;
    }
  }, []);

  const clearInactivityTimers = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (inactivityModalTimeoutRef.current) {
      clearTimeout(inactivityModalTimeoutRef.current);
      inactivityModalTimeoutRef.current = null;
    }
    if (inactivityIntervalRef.current) {
      clearInterval(inactivityIntervalRef.current);
      inactivityIntervalRef.current = null;
    }
  }, []);

  const logout = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    clearStayModalTimers();
    clearInactivityTimers();
    localStorage.removeItem("accesstoken");
    localStorage.removeItem("decodedToken");
    sessionStorage.removeItem("accesstoken");
    sessionStorage.removeItem("decodedToken");
    sessionStorage.removeItem("mfa_pending_username");
    localStorage.removeItem(STORED_PERMISSIONS_KEY);
    localStorage.removeItem(STORED_MENU_ITEMS_KEY);
    clearReAuthSessionState();
    setToken(null);
    setDecodedToken(null);
    setPermissions([]);
    setMenuItems([]);
    setShowStayModal(false);
    setShowPasswordModal(false);
    setShowInactivityModal(false);
    setReAuthAttempts(0);
  }, [clearInactivityTimers, clearStayModalTimers]);

  const scheduleExpiry = useCallback((decoded) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const msUntilExpiry = decoded.exp * 1000 - Date.now();

    if (msUntilExpiry <= 0) {
      const storedAttempts = getStoredReAuthAttempts();
      const storedStage = sessionStorage.getItem(REAUTH_STAGE_KEY);

      if (storedAttempts >= MAX_REAUTH_ATTEMPTS) {
        toast.error("Maximum re-authentication attempts reached. Logging out.");
        logout();
        return;
      }

      if (storedStage === "password" || storedAttempts > 0) {
        setShowStayModal(false);
        setShowPasswordModal(true);
        return;
      }

      sessionStorage.setItem(REAUTH_STAGE_KEY, "stay");
      setShowPasswordModal(false);
      setShowStayModal(true);
      return;
    }

    clearReAuthSessionState();
    setReAuthAttempts(0);
    setShowStayModal(false);
    setShowPasswordModal(false);
    timerRef.current = setTimeout(() => {
      sessionStorage.setItem(REAUTH_STAGE_KEY, "stay");
      setShowStayModal(true);
    }, msUntilExpiry);
  }, [logout]);

  const resetInactivityTimer = useCallback(() => {
    if (!token || !decodedToken) {
      return;
    }

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    if (showInactivityModal) {
      return;
    }

    inactivityTimerRef.current = setTimeout(() => {
      setShowInactivityModal(true);
      setInactivityCountdown(INACTIVITY_COUNTDOWN_SECONDS);
    }, INACTIVITY_TIMEOUT_MS);
  }, [decodedToken, showInactivityModal, token]);

  const promptReAuth = () => {
    clearInactivityTimers();
    setShowInactivityModal(false);
    setShowStayModal(false);
    setReAuthPassword("");
    setReAuthError("");
    setReAuthAttempts(0);
    sessionStorage.setItem(REAUTH_STAGE_KEY, "password");
    setTimeout(() => {
      setShowPasswordModal(true);
    }, 0);
  };

  const handleInactivityStayLoggedIn = () => {
    promptReAuth();
  };

  useEffect(() => {
    if (reAuthAttempts > 0) {
      sessionStorage.setItem(REAUTH_ATTEMPTS_KEY, String(reAuthAttempts));
    } else {
      sessionStorage.removeItem(REAUTH_ATTEMPTS_KEY);
    }
  }, [reAuthAttempts]);

  useEffect(() => {
    if (!showStayModal) {
      clearStayModalTimers();
      setStayCountdown(STAY_MODAL_TIMEOUT_SECONDS);
      return;
    }

    setStayCountdown(STAY_MODAL_TIMEOUT_SECONDS);
    stayIntervalRef.current = setInterval(() => {
      setStayCountdown((previous) => (previous > 0 ? previous - 1 : 0));
    }, 1000);

    stayTimeoutRef.current = setTimeout(() => {
      setShowStayModal(false);
      logout();
    }, STAY_MODAL_TIMEOUT_SECONDS * 1000);

    return () => {
      clearStayModalTimers();
    };
  }, [showStayModal, logout]);

  useEffect(() => {
    if (!showInactivityModal) {
      clearInactivityTimers();
      setInactivityCountdown(INACTIVITY_COUNTDOWN_SECONDS);
      return;
    }

    setInactivityCountdown(INACTIVITY_COUNTDOWN_SECONDS);
    inactivityIntervalRef.current = setInterval(() => {
      setInactivityCountdown((previous) => (previous > 0 ? previous - 1 : 0));
    }, 1000);

    inactivityModalTimeoutRef.current = setTimeout(() => {
      setShowInactivityModal(false);
      setIsActiveSession(false);
      logout();
    }, INACTIVITY_COUNTDOWN_SECONDS * 1000);

    return () => {
      clearInactivityTimers();
    };
  }, [showInactivityModal, logout]);

  useEffect(() => {
    if (token && decodedToken) {
      scheduleExpiry(decodedToken);
      resetInactivityTimer();
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      clearStayModalTimers();
      clearInactivityTimers();
    };
  }, [token, decodedToken, resetInactivityTimer, scheduleExpiry]);

  useEffect(() => {
    const handleActivity = () => {
      if (token && decodedToken && !showInactivityModal) {
        resetInactivityTimer();
      }
    };

    const activityEvents = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity);
    });

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
    };
  }, [decodedToken, resetInactivityTimer, showInactivityModal, token]);

  const persistPermissions = (nextPermissions) => {
    const safePermissions = Array.isArray(nextPermissions) ? nextPermissions : [];
    localStorage.setItem(STORED_PERMISSIONS_KEY, JSON.stringify(safePermissions));
    setPermissions(safePermissions);
  };

  const persistMenuItems = (nextMenuItems) => {
    const safeMenuItems = Array.isArray(nextMenuItems) ? nextMenuItems : [];
    localStorage.setItem(STORED_MENU_ITEMS_KEY, JSON.stringify(safeMenuItems));
    setMenuItems(safeMenuItems);
  };

  const login = (newToken, authData = {}) => {
    const decoded = jwtDecode(newToken);
    localStorage.setItem("accesstoken", newToken);
    localStorage.setItem("decodedToken", JSON.stringify(decoded));
    sessionStorage.removeItem("accesstoken");
    sessionStorage.removeItem("decodedToken");
    clearReAuthSessionState();
    clearStayModalTimers();
    clearInactivityTimers();
    setToken(newToken);
    setDecodedToken(decoded);
    const resolvedPermissions =
      authData.permissions && authData.permissions.length > 0
        ? authData.permissions
        : Array.isArray(decoded?.Permissions || decoded?.permissions)
        ? (decoded.Permissions || decoded.permissions)
        : permissions;
    persistPermissions(resolvedPermissions);
    persistMenuItems(authData.menuItems || menuItems);
    setReAuthAttempts(0);
    setShowStayModal(false);
    setShowPasswordModal(false);
    setShowInactivityModal(false);
    setIsActiveSession(true);
    scheduleExpiry(decoded);
  };

  const handleStayConfirm = () => {
    clearStayModalTimers();
    setShowStayModal(false);
    setShowInactivityModal(false);
    setReAuthPassword("");
    setReAuthError("");
    setReAuthAttempts(0);
    setIsActiveSession(true);
    sessionStorage.setItem(REAUTH_STAGE_KEY, "password");
    setShowPasswordModal(true);
  };

  const handleStayDecline = () => {
    clearStayModalTimers();
    setShowStayModal(false);
    setShowInactivityModal(false);
    logout();
  };

  const handleReAuth = async () => {
    const email = decodedToken?.user_email ||"";

    if (!reAuthPassword.trim()) {
      setReAuthError("Password is required.");
      return;
    }

    if (reAuthAttempts >= MAX_REAUTH_ATTEMPTS) {
      toast.error("Maximum re-authentication attempts reached. Logging out.");
      logout();
      return;
    }

    const handleFailedAttempt = (message) => {
      const nextAttempts = reAuthAttempts + 1;
      const attemptsLeft = MAX_REAUTH_ATTEMPTS - nextAttempts;
      setReAuthAttempts(nextAttempts);
      sessionStorage.setItem(REAUTH_STAGE_KEY, "password");

      if (attemptsLeft <= 0) {
        toast.error("Maximum re-authentication attempts reached. Logging out.");
        logout();
        return;
      }

      setReAuthError(`${message} ${attemptsLeft} attempt left.`);
    };

    setReAuthLoading(true);
    setReAuthError("");

    try {
      const response = await loginApi({ username: email, password: reAuthPassword });
      if (response?.is_success) {
        login(token);
        setShowPasswordModal(false);
        setReAuthPassword("");
        setReAuthAttempts(0);
      } else {
        handleFailedAttempt("Invalid password.");
      }
    } catch {
      handleFailedAttempt("Authentication failed.");
    } finally {
      setReAuthLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ token, decodedToken, permissions, menuItems, login, logout, persistPermissions, persistMenuItems }}>
      {children}
      {showStayModal && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1055 }} />
          <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1056 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content rounded-4 border-0 shadow-lg">
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold hc-text-primary">Session Expired</h5>
                </div>
                <div className="modal-body py-3">
                  <p className="text-secondary mb-0">
                    Your session has expired. Would you like to stay logged in?
                  </p>
                </div>
                <div className="modal-footer border-0 pt-0 gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm rounded-3 px-3"
                    onClick={handleStayDecline}
                  >
                    Logout
                  </button>
                  <button
                    type="button"
                    className="btn hc-bg-primary text-white btn-sm rounded-3 px-3"
                    onClick={handleStayConfirm}
                  >
                    Stay Logged In ({stayCountdown})
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showInactivityModal && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1055 }} />
          <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1056 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content rounded-4 border-0 shadow-lg">
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold hc-text-primary">Inactivity detected</h5>
                </div>
                <div className="modal-body py-3">
                  <p className="text-secondary mb-0">
                    We've detected inactivity on this site. Would you like to stay logged in?
                  </p>
                </div>
                <div className="modal-footer border-0 pt-0 gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm rounded-3 px-3"
                    onClick={() => {
                      setShowInactivityModal(false);
                      logout();
                    }}
                  >
                    Logout
                  </button>
                  <button
                    type="button"
                    className="btn hc-bg-primary text-white btn-sm rounded-3 px-3"
                    onClick={handleInactivityStayLoggedIn}
                  >
                    Stay Logged In ({inactivityCountdown})
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <ReAuthModal
        isOpen={showPasswordModal}
        description="Enter your password to continue your session."
        password={reAuthPassword}
        onPasswordChange={(value) => {
          setReAuthPassword(value);
          setReAuthError("");
        }}
        showPassword={showReAuthPassword}
        onTogglePassword={() => setShowReAuthPassword((v) => !v)}
        error={reAuthError}
        loading={reAuthLoading}
        attemptsRemaining={Math.max(0, MAX_REAUTH_ATTEMPTS - reAuthAttempts)}
        maxAttempts={MAX_REAUTH_ATTEMPTS}
        onCancel={() => {
          setShowPasswordModal(false);
          logout();
        }}
        onConfirm={handleReAuth}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);